import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { IIngkaToken } from '@inter-ikea-kompis/types';

import IframeManager from '../../../utilities/IframeManager.js';

/**
 * This class is used for fetching authentication data.
 */
export default class OneWebAuthenticationService {
	private readonly endpoint: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.endpoint Endpoint.
	 */
	constructor(options: { endpoint: string }) {
		this.endpoint = options.endpoint;
	}

	/**
	 * Authenticates and returns token.
	 *
	 * @returns Promise.
	 */
	public async getToken(): Promise<IIngkaToken> {
		// When the INGKA Javascript API is available on the same page, we can use it to retrieve the token.
		// This is the case when the footer fragment from INGKA is included using ESI include, or if INGKA includes the planner directly on the page using ESI include.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).ikea?.authentication?.getTokens) {
			return await this.getTokenFromIngkaApi(window);
		}

		// When a planner is running under /addon-app/, it is not possible to include the footer fragment from INGKA as we are not under a retail unit path (e.g. /gb/en/).
		// It is also not possible to include the necessary Javascript files as the INGKA API will not be able to read the cookies (as they are set under a retail unit path).
		// In this case, we need to use an iframe with a URL that contains a retail unit path to load the INGKA Javascript API and retrieve the token from there.
		// It is only possible to use this method if the planner is running on the same origin as the endpoint for the iframe as it will otherwise not be possible to access the content window.
		// It will for example not work if a subdomain (e.g. "planner.ikea.com") includes an iframe URL with the domain "www.ikea.com".
		const iframeManager = new IframeManager(this.endpoint);
		const iframe = await iframeManager.getIframe();
		const token = await this.getTokenFromIngkaApi(<Window>iframe.contentWindow);

		iframeManager.disconnect();

		return token;
	}

	/**
	 * Returns token from INGKA Javascript API.
	 *
	 * @param targetWindow Window.
	 * @returns Token.
	 */
	private getTokenFromIngkaApi(targetWindow: Window): Promise<IIngkaToken> {
		return new Promise((resolve, reject) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			if (!(targetWindow as any).ikea?.authentication?.getTokens) {
				reject(
					new ServiceException(
						'Failed to retrieve token from iframe. "ikea.authentication.getTokens()" is not defined.'
					)
				);
				return;
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(targetWindow as any).ikea.authentication.getTokens((error, tokens): void => {
				if (error) {
					reject(new ServiceException('Failed to retrieve token from iframe. Error: ' + error));
					return;
				}

				const token = tokens.auth ? tokens.auth : tokens.guest;

				resolve({
					value: token.value,
					isGuest: !tokens.auth
				});
			});
		});
	}
}
